import { useModel } from "@stem-sims/nexus"
import { Button, Col, Row } from "react-bootstrap"
import { PulseLoader } from "react-spinners"
import { useCallback, useState } from "react"
import EditingItemPane, { FieldType } from "../EditingItemPane"
import { createReview, deleteReview, getBlocks, getReviewGroup, updateReview, updateReviewGroup } from "shared/routes/reviewRoutes"
import EditingListPane from "../EditingListPane"
import { useParams } from "react-router"
import { toast } from "react-toastify"
import assessments from "shared/routes/assessments/assessments"
import SelectModal from "../Components/SelectModal"
import lessons from "shared/lessons"
import { EnableReadingLevelBlocks } from "shared/types/science35Types"

export default function EditingReviewGroup() {
    const { groupId } = useParams() as { groupId: string }

    const [selectedReviewId, setSelectedReviewId] = useState<string>(null)
    const [selectModal, setSelectModal] = useState<"lesson" | "grade3lesson" | "assessment" | "grade3assessment" | "postAssessment" | "grade3postAssessment">(null)

    const { loading: loadingBlocks, response: blocks } = useModel({
        model: getBlocks,
    })

    const { loading, response: reviewGroup, refreshModel: refresh } = useModel({
        model: getReviewGroup,
        props: groupId,
    })

    const loadingComplete = (!loading && reviewGroup)

    const reviews = loadingComplete ? reviewGroup.reviews?.map((review) => ({ id: review.id, name: review.title })) : []
    const review = loadingComplete ? reviewGroup.reviews?.find(r => r.id === selectedReviewId) : null

    const { response: assessment } = useModel({
        model: assessments.findById,
        props: { id: review?.pretestId },
    })
    
    const { response: lesson } = useModel({
        model: lessons.findById,
        props: review?.lessonId ?? undefined,
    })

    const { response: postAssessment } = useModel({
        model: assessments.findById,
        props: { id: review?.postTestId },
    })

    const { response: grade3assessment } = useModel({
        model: assessments.findById,
        props: { id: review?.grade3pretestId },
    })

    const { response: grade3lesson } = useModel({
        model: lessons.findById,
        props: review?.grade3lessonId ?? undefined,
    })

    const { response: grade3postAssessment } = useModel({
        model: assessments.findById,
        props: { id: review?.grade3postTestId },
    })

    const updateReviewCallback = useCallback(async (values: { id: string }) => {
        setSelectedReviewId(selectedReviewId === values.id ? null : values.id)
    }, [selectedReviewId])

    const createReviewCallback = useCallback(async () => {
        try {
            const message = await createReview([groupId])
            toast.success(message)
            refresh()
        } catch (err) {
            toast.error(err)
        }
    }, [refresh, groupId])

    const onSubmitReviewGroupCallback = useCallback(async (values: { name: string, description: string, blocks: string[] }) => {
        try {
            await updateReviewGroup({ 
                groupId, 
                name: values.name, 
                description: values.description, 
                blocks: values.blocks,
            })
        } catch (err) {
            toast.error("Something went wrong updating review group information.")
        }
        refresh()
    }, [refresh, groupId])

    const onSubmitReviewCallback = useCallback(async (values: { title: string, pretestId: string, lessonId: string, postTestId: string, grade3pretestId: string, grade3lessonId: string, grade3postTestId: string, image: string }) => {
        try {
            await updateReview({
                reviewId: selectedReviewId,
                title: values.title,
                pretestId: values.pretestId ?? undefined,
                postTestId: values.postTestId ?? undefined,
                lessonId: values.lessonId ?? undefined,
                grade3pretestId: values.grade3pretestId ?? undefined,
                grade3lessonId: values.grade3lessonId ?? undefined,
                grade3postTestId: values.grade3postTestId ?? undefined,
                image: values.image ?? undefined,
                groupIds: review?.groupIds ?? [],
            })
        } catch {
            toast.error("Something went wrong updating review information.")
        }
        refresh()
    }, [refresh, selectedReviewId, review])

    const deleteReviewCallback = useCallback(async (values: { id: string }) => {
        try {
            const message = await deleteReview(values.id)
            toast.success(message)
            refresh()
        } catch (err) {
            toast.error(err)
        }
    }, [refresh])

    if (loading || !reviewGroup || loadingBlocks || !blocks) {
        return <>
            <PulseLoader />
        </>
    }

    const reviewItemFields: FieldType[] = [
        { fieldName: "title", inputType: "input" },
        {
            fieldName: "pretestId", inputType: "special", render: () => (<>
                {assessment && <> 
                    <span className="ms-2 text-primary fw-bold">{assessment.id}</span>
                    <p className="my-0">Selected: {assessment.module} - {assessment.type} - #{assessment.number}</p>
                </>}
                {!assessment && <span className="text-danger"> Not Selected</span>}
                <Button variant="theme" className="d-block" onClick={() => { setSelectModal("assessment") }}>
                    Select Assessment
                </Button>
            </>)
        },
        { 
            fieldName: "lessonId", inputType: "special", render: () => (<>
               {lesson && <> 
                    <span className="ms-2 text-primary fw-bold">{lesson.id}</span>
                    <p className="my-0">Selected: {lesson.moduleName} - {lesson.type} - #{lesson.number} ({lesson.title})</p>
                </>}
                    {!lesson && <span className="text-danger"> Not Selected</span>}
                    <Button variant="theme" className="d-block" onClick={() => { setSelectModal("lesson") }}>
                        Select Lesson
                    </Button>
            </>)
        },
        {
            fieldName: "postTestId", inputType: "special", render: () => (<>
                {postAssessment && <>
                    <span className="ms-2 text-primary fw-bold">{postAssessment.id}</span>
                    <p className="my-0">Selected: {postAssessment.module} - {postAssessment.type} - #{postAssessment.number} ({postAssessment.title})</p>
                </>}
                {!postAssessment && <span className="text-warning"> Not Selected</span>}
                <Button variant="theme" className="d-block" onClick={() => { setSelectModal("postAssessment") }}>
                    Select postAssessment
                </Button>
            </>)
        },
        { 
            fieldName: "grade 3 pretestId", inputType: "special", render: () => (<>
               {grade3assessment && <> 
                    <span className="ms-2 text-primary fw-bold">{grade3assessment.id}</span>
                    <p className="my-0">Selected: {grade3assessment.module} - {grade3assessment.type} - #{grade3assessment.number}</p>
                </>}
                {!grade3assessment && <span className="text-warning"> Not Selected</span>}
                <span className="mx-0 row justify-content-between">
                    <Button variant="theme" className="col-auto" onClick={() => { setSelectModal("grade3assessment") }}>
                        Select Grade 3 Assessment
                    </Button>
                    {grade3assessment && <Button variant="secondary" className="col-auto" onClick={async() => {
                        await updateReview({
                            reviewId: selectedReviewId,
                            grade3pretestId: "deselect"
                        })
                        refresh()
                        toast.success("Deselected successfully.")
                    }}>
                        Deselect Grade 3 Assessment
                    </Button>}
                </span>
            </>)
        },
        { 
            fieldName: "grade 3 lessonId", inputType: "special", render: () => (<>
               {grade3lesson && <> 
                    <span className="ms-2 text-primary fw-bold">{grade3lesson.id}</span>
                    <p className="my-0">Selected: {grade3lesson.moduleName} - {grade3lesson.type} - #{grade3lesson.number} ({grade3lesson.title})</p>
                </>}
                {!grade3lesson && <span className="text-warning"> Not Selected</span>}
                <span className="mx-0 row justify-content-between">
                    <Button variant="theme" className="col-auto" onClick={() => { setSelectModal("grade3lesson") }}>
                        Select Grade 3 Lesson
                    </Button>
                    {grade3lesson && <Button variant="secondary" className="col-auto" onClick={async() => {
                        await updateReview({
                            reviewId: selectedReviewId,
                            grade3lessonId: "deselect"
                        })
                        refresh()
                        toast.success("Deselected successfully.")
                    }}>
                        Deselect Grade 3 Lesson
                    </Button>}
                </span>
            </>)
        },
        { 
            fieldName: "grade 3 postTestId", inputType: "special", render: () => (<>
               {grade3postAssessment && <> 
                    <span className="ms-2 text-primary fw-bold">{grade3postAssessment.id}</span>
                    <p className="my-0">Selected: {grade3postAssessment.module} - {grade3postAssessment.type} - #{grade3postAssessment.number} ({grade3postAssessment.title})</p>
                </>}
                {!grade3postAssessment && <span className="text-warning"> Not Selected</span>}
                <span className="mx-0 row justify-content-between">
                    <Button variant="theme" className="col-auto" onClick={() => { setSelectModal("grade3postAssessment") }}>
                        Select Grade 3 postAssessment
                    </Button>
                    {grade3postAssessment && <Button variant="secondary" className="col-auto" onClick={async() => {
                        await updateReview({
                            reviewId: selectedReviewId,
                            grade3postTestId: "deselect"
                        })
                        refresh()
                        toast.success("Deselected successfully.")
                    }}>
                        Deselect Grade 3 postAssessment
                    </Button>}
                </span>
            </>)
        },
        { fieldName: "image", inputType: "image" },
    ]

    return <>
        <Row>
            <Col xs={6}>
                <EditingItemPane
                    data={{ ...reviewGroup }}
                    fields={[
                        { fieldName: "name", inputType: "input" },
                        { fieldName: "description", inputType: "input" },
                        { fieldName: "blocks", inputType: "multi-select", options: blocks.map(b => b.block), selectedOptions: reviewGroup.blocks, label: "Select Blocks" },
                    ]}
                    itemTypeName="Review Group"
                    onSubmit={onSubmitReviewGroupCallback}
                />
                {!selectedReviewId && <h3 className="text-muted mt-5">Select a review to see its details.</h3>}
                {selectedReviewId && 
                    <div>
                        <hr className="my-5" />
                        <EditingItemPane
                            data={{
                                ...review,
                            }}
                            fields={
                                reviewGroup.blocks.some(a => EnableReadingLevelBlocks.includes(a.block)) ?
                                reviewItemFields : reviewItemFields.filter(a => !a.fieldName.includes("grade 3"))
                            }
                            itemTypeName="review"
                            onSubmit={onSubmitReviewCallback}
                        />
                    </div>}
                <SelectModal 
                    selectModal={selectModal} 
                    onHide={() => setSelectModal(null)}
                    includeUnlinkedAssessments={["assessment", "postAssessment", "grade3assessment", "grade3postAssessment"].includes(selectModal) ? true : undefined}
                    onSelection={(selectedLesson, _selectedStudent, selectedAssessment) => {
                        return (
                            <Button
                                onClick={async () => {
                                    const selectedAssessmentId = selectModal === "assessment" ? (selectedAssessment?.id ?? selectedLesson?.assessment?.id) : undefined
                                    const selectedLessonId = selectModal === "lesson" ? selectedLesson?.id : undefined
                                    const selectedPostAssessmentId = selectModal === "postAssessment" ? (selectedAssessment?.id ?? selectedLesson?.assessment?.id) : undefined
                                    const selectedGrade3AssessmentId = selectModal === "grade3assessment" ? (selectedAssessment?.id ?? selectedLesson?.assessment?.id) : undefined
                                    const selectedGrade3LessonId = selectModal === "grade3lesson" ? selectedLesson?.id : undefined
                                    const selectedGrade3PostAssessmentId = selectModal === "grade3postAssessment" ? (selectedAssessment?.id ?? selectedLesson?.assessment?.id) : undefined

                                    await updateReview({
                                        reviewId: selectedReviewId,
                                        pretestId: selectedAssessmentId,
                                        lessonId: selectedLessonId,
                                        postTestId: selectedPostAssessmentId,
                                        grade3pretestId: selectedGrade3AssessmentId,
                                        grade3lessonId: selectedGrade3LessonId,
                                        grade3postTestId: selectedGrade3PostAssessmentId
                                    })
                                    refresh()
                                    setSelectModal(null)
                                }}
                            >Select {selectModal}</Button>
                        )
                    }} />
            </Col>
            <Col xs={6}>
                <h4>Reviews</h4>
                <EditingListPane
                    parent={null}
                    items={reviews}
                    itemType="review"
                    createItem={createReviewCallback}
                    updateItem={updateReviewCallback}
                    deleteItem={deleteReviewCallback}
                />
            </Col>
        </Row>
    </>
}
